import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import ViewAffiliations from '@/components/demo-club/ViewAffiliations';
import ViewDashboard from '@/components/demo-club/ViewDashboard';
import ViewEquipes from '@/components/demo-club/ViewEquipes';
import ViewMembres from '@/components/demo-club/ViewMembres';
import { ROUTES } from '@/config/routes.ts';
import { ArrowRight, CheckCircle, Download, Pause, Play, Trophy, Users } from 'lucide-react';

// ─── Types & constantes ───────────────────────────────────────────────────────
const TABS = ['membres', 'equipes', 'affiliations', 'dashboard'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  membres: '👥 Membres',
  equipes: '✅ Équipes',
  affiliations: '🤝 Affiliations',
  dashboard: '📊 Dashboard',
};

const TAB_SUBTITLES: Record<Tab, string> = {
  membres: 'Gérer le club · Données FFPJP · Annuaire licenciés',
  equipes: 'Composer et inscrire des équipes · En 2 minutes',
  affiliations: "Demandes d'adhésion · Clubs affiliés · Concours partenaires",
  dashboard: 'Stats club · Exports · Fonctionnalités avancées',
};

// ─── Page principale ──────────────────────────────────────────────────────────
const BENEFITS = [
  {
    icon: Users,
    title: 'Membres centralisés',
    desc: 'Votre liste de licenciés, propre et à jour. Ajout, invitations, vérification, exports.',
    gradient: 'from-blue-500/10 to-blue-600/5',
    border: 'border-blue-500/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-500/10',
  },
  {
    icon: CheckCircle,
    title: 'Équipes en 1 clic',
    desc: 'Préparez des équipes, affectez des capitaines, inscrivez-les sans erreurs ni ressaisie.',
    gradient: 'from-emerald-500/10 to-emerald-600/5',
    border: 'border-emerald-500/20',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-500/10',
  },
  {
    icon: Trophy,
    title: 'Affiliations & partenariats',
    desc: "Demandes d'adhésion en ligne, validation en un clic. Accès restreint aux membres affiliés.",
    gradient: 'from-amber-500/10 to-amber-600/5',
    border: 'border-amber-500/20',
    iconColor: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-amber-500/10',
  },
  {
    icon: Download,
    title: 'Exports & reporting',
    desc: 'CSV prêts à l’emploi : membres, inscriptions, classements. Gain de temps immédiat.',
    gradient: 'from-purple-500/10 to-purple-600/5',
    border: 'border-purple-500/20',
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-500/10',
  },
] as const;

export default function DemoClub() {
  const [activeTab, setActiveTab] = useState<Tab>('membres');
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  const isPausedRef = useRef(false);
  const activeTabRef = useRef<Tab>('membres');
  const progressRef = useRef(0);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    activeTabRef.current = tab;
    progressRef.current = 0;
    setProgress(0);
  };

  const togglePause = () => {
    const next = !isPausedRef.current;
    isPausedRef.current = next;
    setIsPaused(next);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPausedRef.current) return;

      progressRef.current += 0.5;
      setProgress(progressRef.current);

      if (progressRef.current >= 100) {
        progressRef.current = 0;
        const idx = TABS.findIndex(t => t === activeTabRef.current);
        const nextTab = TABS[(idx + 1) % TABS.length];
        activeTabRef.current = nextTab;
        setActiveTab(nextTab);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <PageMeta
        title='Démo Club'
        description="Découvrez l'expérience BoulesConnect pour les clubs : gestion des membres, composition d'équipes, affiliations et dashboard avec exports CSV."
        robots='noindex, nofollow'
      />

      <div className='min-h-screen bg-white dark:bg-[#030712] text-slate-900 dark:text-white'>
        {/* ─── Hero ─── */}
        <section className='pt-32 pb-14 px-6 text-center'>
          <div className='max-w-4xl mx-auto'>
            {/* Role selector */}
            <div className='flex items-center justify-center flex-wrap gap-3 mb-12'>
              <Link
                to={'/demo'}
                className='border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-600 px-5 py-2 rounded-full font-bold text-sm'
              >
                🧑‍🤝‍🧑 Joueur
              </Link>

              <button className='bg-linear-to-br from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-full font-bold text-sm flex items-center gap-2 shadow-lg shadow-blue-600/30 dark:shadow-blue-900/40'>
                🏟️ Club
                <span className='bg-white/20 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider'>
                  Live
                </span>
              </button>

              <Link
                to={'/demo-organisateur'}
                className='border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-600 px-5 py-2 rounded-full font-bold text-sm'
              >
                🏆 Organisateur
              </Link>
            </div>

            <p className='text-blue-600 dark:text-blue-400 font-bold tracking-[0.2em] uppercase text-xs mb-5'>
              Démo interactive · Rôle Club
            </p>

            <h1 className='text-4xl md:text-6xl font-black tracking-tight leading-[1.05] mb-6 text-slate-900 dark:text-blue-400'>
              Un club doit pouvoir
              <br />
              <span className='text-transparent bg-clip-text bg-linear-to-r from-blue-500 via-indigo-500 to-emerald-500 dark:from-blue-400 dark:via-indigo-400 dark:to-emerald-400'>
                gérer, engager, et performer.
              </span>
            </h1>

            <p className='text-slate-500 dark:text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed'>
              Membres, équipes, affiliations et exports CSV : le club gagne du temps, réduit les erreurs, et suit ses
              résultats.
            </p>
          </div>
        </section>

        {/* ─── Demo simulator ─── */}
        <section className='px-4 md:px-10 pb-24'>
          <div className='max-w-7xl mx-auto'>
            <div className='relative overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl shadow-slate-200/60 dark:border-slate-800 dark:bg-[#0B1120] dark:shadow-black/60'>
              {/* Tab nav */}
              <div className='flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-slate-50/70 px-5 py-4 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/70'>
                <div className='min-w-0'>
                  <p className='text-[9px] font-black uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400'>
                    Parcours club
                  </p>
                  <p className='mt-0.5 truncate text-sm font-bold text-slate-600 dark:text-white'>
                    {TAB_SUBTITLES[activeTab]}
                  </p>
                </div>

                <div className='flex shrink-0 gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white/80 p-1.5 dark:border-slate-700 dark:bg-slate-800/80'>
                  {TABS.map((tab, i) => (
                    <button
                      key={tab}
                      onClick={() => switchTab(tab)}
                      className={[
                        'flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl px-4 py-2 text-xs font-bold transition-all',
                        activeTab === tab
                          ? 'bg-blue-600 text-white shadow-[0_4px_14px_rgba(59,130,246,0.35)]'
                          : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'text-[10px] font-black',
                          activeTab === tab ? 'text-blue-200' : 'text-slate-400 dark:text-slate-600',
                        ].join(' ')}
                      >
                        {i + 1}
                      </span>
                      {TAB_LABELS[tab]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className='min-h-125 p-6 md:p-10 text-slate-900 dark:text-slate-50'>
                {activeTab === 'membres' && <ViewMembres key='membres' />}
                {activeTab === 'equipes' && <ViewEquipes key='equipes' />}
                {activeTab === 'affiliations' && <ViewAffiliations key='affiliations' />}
                {activeTab === 'dashboard' && <ViewDashboard key='dashboard' />}
              </div>

              {/* Playback bar */}
              <div className='flex items-center gap-5 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-900/80'>
                <button
                  onClick={togglePause}
                  aria-label={isPaused ? 'Reprendre' : 'Pause'}
                  className={[
                    'rounded-full border p-2 transition hover:scale-110',
                    'border-slate-200 bg-white text-slate-600 hover:text-blue-600',
                    'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-blue-400',
                    isPaused ? 'ring-2 ring-blue-500/20' : '',
                  ].join(' ')}
                >
                  {isPaused ? (
                    <Play className='h-4 w-4 fill-current ml-0.5' />
                  ) : (
                    <Pause className='h-4 w-4 fill-current' />
                  )}
                </button>

                <div className='h-1 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800'>
                  <div
                    className={[
                      'h-full rounded-full bg-linear-to-r from-blue-500 via-indigo-500 to-emerald-400 transition-all duration-100 ease-linear',
                      isPaused ? 'opacity-30' : '',
                    ].join(' ')}
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div className='flex items-center gap-2'>
                  {TABS.map(tab => (
                    <button
                      key={tab}
                      onClick={() => switchTab(tab)}
                      aria-label={TAB_LABELS[tab]}
                      className={[
                        'rounded-full transition-all',
                        activeTab === tab
                          ? 'h-1.5 w-4 bg-blue-500 dark:bg-blue-400'
                          : 'h-1.5 w-1.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-500',
                      ].join(' ')}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Benefits ─── */}
        <section className='px-6 py-24 bg-slate-50 dark:bg-[#040B18]'>
          <div className='max-w-6xl mx-auto'>
            <div className='text-center mb-16'>
              <p className='text-blue-600 dark:text-blue-400 font-bold text-xs uppercase tracking-[0.2em] mb-5'>
                Pourquoi BoulesConnect (Club)
              </p>
              <h2 className='text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-snug'>
                Le club reprend le contrôle
                <br />
                <span className='text-transparent bg-clip-text bg-linear-to-r from-blue-500 to-emerald-500 dark:from-blue-400 dark:to-emerald-400'>
                  de son organisation sportive.
                </span>
              </h2>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-5'>
              {BENEFITS.map(({ icon: Icon, title, desc, gradient, border, iconColor, iconBg }) => (
                <div
                  key={title}
                  className={`bg-linear-to-br ${gradient} ${border} border rounded-3xl p-7 hover:-translate-y-1 transition-all`}
                >
                  <div className={`w-11 h-11 ${iconBg} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                  </div>
                  <h3 className='font-black text-slate-900 dark:text-white text-lg mb-3 leading-snug'>{title}</h3>
                  <p className='text-slate-500 text-sm leading-relaxed'>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── CTA final ─── */}
        <section className='px-6 py-28 text-center'>
          <div className='max-w-xl mx-auto'>
            <p className='text-slate-500 dark:text-slate-600 text-sm uppercase tracking-widest font-bold mb-5'>
              C'est gratuit pour commencer
            </p>
            <h2 className='text-3xl md:text-4xl font-black text-slate-900 dark:text-white mb-6 leading-snug'>
              Prêt à gérer ton club ?
            </h2>
            <p className='text-slate-500 text-lg mb-10'>
              Compte club créé en 30 secondes. Aucune carte bancaire requise.
            </p>

            <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
              <Link
                to={ROUTES.REGISTER}
                className='w-full sm:w-auto bg-linear-to-r from-blue-600 to-indigo-600 text-white px-10 py-4 rounded-2xl font-black text-base flex items-center justify-center gap-2 hover:shadow-2xl hover:shadow-blue-600/40 dark:hover:shadow-blue-900/50 hover:-translate-y-0.5 transition-all'
              >
                Créer mon compte club <ArrowRight className='w-5 h-5' />
              </Link>

              <Link
                to={ROUTES.TARIFS}
                className='w-full sm:w-auto bg-slate-100 border border-slate-200 text-slate-700 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300 px-10 py-4 rounded-2xl font-bold text-base hover:bg-slate-200 dark:hover:bg-slate-800 transition-all'
              >
                Voir les tarifs
              </Link>
            </div>

            <p className='mt-8 text-slate-500 dark:text-slate-700 text-sm'>
              Accès Club gratuit pour toujours · Offre Club Pro à 20 €/mois
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
